import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../api-client";
import type { CreateInstitutionParams, Institution } from "@/types/institution";

export const institutionKeys = {
  all: ["institution"] as const,
  detail: (id?: string) => [...institutionKeys.all, "detail", id] as const,
};

export function useInstitution(id?: string) {
  return useQuery({
    queryKey: institutionKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<Institution>(`/institutions/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useInstitutions(enabled?: boolean) {
  return useQuery({
    queryKey: institutionKeys.all,
    queryFn: async () => {
      const response = await apiClient.get<Institution[]>(`/institutions`);
      return response.data;
    },
    staleTime: 5_000 * 60, // 5 minutes
    enabled: enabled ?? true,
  });
}

export function useCreateInstitutionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateInstitutionParams) => {
      const response = await apiClient.post<Institution>("/institutions", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: institutionKeys.all });
    },
  });
}

/**
 * Update an existing project
 * Uses: PATCH /projects/:id
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      data,
    }: {
      userId: string;
      data: FormData;
    }): Promise<Institution> => {
      const response = await apiClient.patch(`/users/${userId}`, data);
      return response.data;
    },
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({
        queryKey: institutionKeys.detail(userId),
      });
    },
  });
}
